<script setup lang="ts">
import type { InputSizeType } from '#imports';

interface IProps {
    label?: string;
    description?: string;
    disabled?: boolean;
    invalid?: boolean;
    success?: boolean;
    size?: InputSizeType;
    ghost?: boolean;
    hideDescription?: boolean;
    flatRight?: boolean;
    flatLeft?: boolean;
    fullWidth?: boolean;
    resizable?: boolean;
    scrollToContainer?: boolean;
}
const {
    label = '',
    description = '',
    disabled,
    invalid,
    success,
    size = InputSize.dense,
    ghost,
    hideDescription,
    flatRight,
    flatLeft,
    fullWidth,
    resizable,
    scrollToContainer = false,
} = defineProps<IProps>();

const emit = defineEmits<{
    click: [event: MouseEvent];
}>();

const container = ref<HTMLInputElement | HTMLTextAreaElement>();
const onClick = (event: MouseEvent) => {
    emit('click', event);
    if (scrollToContainer) {
        setTimeout(() => {
            if (container.value) {
                const rect = container.value.getBoundingClientRect();
                if (
                    !(
                        rect.top >= 0 &&
                        rect.left >= 0 &&
                        rect.bottom <=
                            (window.innerHeight ||
                                document.documentElement.clientHeight) &&
                        rect.right <=
                            (window.innerWidth ||
                                document.documentElement.clientWidth)
                    )
                ) {
                    container.value!.scrollIntoView();
                }
            }
        }, 500);
    }
};
</script>

<template>
    <div
        ref="container"
        class="mr-inputs-container"
        :class="[
            {
                disabled,
                invalid,
                success,
                ghost,
                'flat-right': flatRight,
                'flat-left': flatLeft,
                resizable,
            },
            size,
        ]"
        :data-invalid-scroll="invalid"
        @click="onClick($event)"
    >
        <div v-if="label" class="tg-m-500 label">
            {{ label }}
        </div>
        <div
            class="input-wrapper"
            :class="[size, { ghost, 'full-width': fullWidth }]"
        >
            <slot />
        </div>
        <slot name="custom" />
        <div v-if="!hideDescription" class="description">
            {{ description || '&nbsp;' }}
        </div>
    </div>
</template>

<style scoped lang="scss">
.mr-inputs-container {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: fit-content;
    &.resizable {
        resize: both;
        overflow: auto;
    }
    &.disabled {
        .label,
        .description {
            color: usePalette($p-gray, $p-500);
        }
        .input-wrapper {
            background-color: usePalette($p-gray, $p-100);
        }
    }

    &.invalid {
        .description {
            color: usePalette($p-red, $p-500);
        }
        :not(.ghost).input-wrapper {
            background-color: usePalette($p-red, $p-100);
            border: 1px solid usePalette($p-red, $p-500);
        }
    }
    &.success {
        .description {
            color: usePalette($p-green, $p-500);
        }
        .input-wrapper {
            background-color: usePalette($p-green, $p-100);
            border: 1px solid usePalette($p-green, $p-500);
        }
    }
    &:not(.disabled):not(.invalid):not(.ghost):hover {
        .input-wrapper {
            background-color: usePalette($p-blue, $p-100);
            &:deep() {
                input::placeholder {
                    color: usePalette($p-cool-gray, $p-700) !important;
                }
            }
        }
    }

    &.flat-right {
        .input-wrapper {
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
        }
    }
    &.flat-left {
        .input-wrapper {
            border-top-left-radius: 0 !important;
            border-bottom-left-radius: 0 !important;
        }
    }
}

.label {
    margin-bottom: 4px;
    white-space: nowrap;
}

.input-wrapper {
    flex-shrink: 0;
    flex-grow: 1;
    display: flex;
    align-items: center;
    border-radius: 8px;
    height: 48px;
    width: 100%;
    background-color: usePalette($p-cool-gray, $p-100);
    &.ghost {
        background-color: transparent;
    }

    &.free {
        height: fit-content;
        width: fit-content;
    }

    &.full-width {
        width: 100%;
    }

    &:focus-within:not(.ghost) {
        background-color: usePalette($p-blue, $p-100);
    }
}

.description {
    margin-top: 4px;
    min-height: 17px;
    // text
    @include tg-s($weight: $w7, $color: usePalette($p-cool-gray, $p-600));
    line-height: unset;
}

:deep(input) {
    &::placeholder {
        @include set-font($important: true);
        @include tg-m($w5, usePalette($p-cool-gray, $p-500));
        color: usePalette($p-cool-gray, $p-500) !important;
    }

    &:focus {
        &::placeholder {
            color: usePalette($p-cool-gray, $p-700) !important;
        }
    }

    &:disabled,
    &:read-only {
        &::placeholder {
            color: usePalette($p-gray, $p-500) !important;
        }
    }
}
</style>
